import { IPositionType } from '../generated-types'
import { IPositionDrawing, IPositionDrawingQueryParams } from '../generated-types/position-drawing'
import { queryClient } from '../query-client'
import { apiConsts } from './apiConsts'
import { IPaginatedResult } from './paginated-result'
import { generateQueryKey, useDelete, useGet, usePost, usePut } from './reactQuery'

const baseAdminUrl = () => 'admin/position-drawings'
const baseUrl = (customerId, mooringId) => `customers/${customerId}/moorings/${mooringId}/position-drawing`

export const positionDrawingEndpoints = {
  getPositionDrawings() {
    return baseAdminUrl()
  },
  getPositionDrawingsForMooring(searchInput?: string) {
    return `${baseAdminUrl()}/getPositionDrawingsForMooring?searchInput=${searchInput ?? ''}`
  },
  getSimplePositionDrawings(searchInput?: string) {
    return `${baseAdminUrl()}/getSimplePositionDrawings?searchInput=${searchInput ?? ''}`
  },
  getDropDownPositionDrawings(searchInput?: string) {
    return `${baseAdminUrl()}/getDropDownPositionDrawings?searchInput=${searchInput ?? ''}`
  },
  getPositionDrawing(id: EntityId) {
    return `${baseAdminUrl()}/${id}`
  },
  createPositionDrawing() {
    return `${baseAdminUrl()}`
  },
  updatePositionDrawing(id: EntityId) {
    return `${baseAdminUrl()}/${id}`
  },
  deletePositionDrawing() {
    return `${baseAdminUrl()}`
  },
  getPositionDrawingInMooring(customerId, mooringId) {
    return baseUrl(customerId, mooringId)
  },
  getSimplePositionDrawingsForProjects() {
    return `${baseAdminUrl()}/get-simple-position-drawings-for-projects`
  },
}

export const invalidatePositionDrawingCache = {
  useGetPositionDrawings() {
    queryClient.invalidateQueries(positionDrawingEndpoints.getPositionDrawings())
  },
  getPositionDrawingById(positionDrawingId: EntityId) {
    queryClient.invalidateQueries(positionDrawingEndpoints.getPositionDrawing(positionDrawingId))
  },
  getPositionDrawingsForMooring() {
    queryClient.invalidateQueries(positionDrawingEndpoints.getPositionDrawingsForMooring())
  },
  getSimplePositionDrawings() {
    queryClient.invalidateQueries(positionDrawingEndpoints.getSimplePositionDrawings())
    queryClient.invalidateQueries(positionDrawingEndpoints.getDropDownPositionDrawings())
  },
  getPositionDrawingInMooring(customerId?: EntityId, mooringId?: EntityId) {
    queryClient.invalidateQueries({
      queryKey: generateQueryKey('position-drawings-in-mooring', {
        customerId,
        mooringId,
      }),
    })
  },
}

const hasValidMooringId = (mooringId?: EntityId) =>
  mooringId != null && mooringId !== '' && Number(mooringId) > 0

export const useGetPositionDrawings = (
  searchInput?: string,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  positionType?: IPositionType,
  mooringId?: EntityId,
  customerId?: EntityId,
) =>
  useGet<IPaginatedResult<IPositionDrawing>>(positionDrawingEndpoints.getPositionDrawings(), {
    page,
    pageSize,
    searchInput,
    positionType,
    mooringId,
    customerId,
  })

export const useGetPositionDrawingsForMooring = (
  mooringId?: EntityId,
  searchInput?: string,
  positionType?: IPositionType,
  page: number = apiConsts.Page,
  pageSize: number = apiConsts.PageSize,
  productCategoryIds?: EntityId[],
) =>
  useGet<IPaginatedResult<IPositionDrawing>>(positionDrawingEndpoints.getPositionDrawingsForMooring(searchInput), {
    mooringId,
    positionType,
    page,
    pageSize,
    productCategoryIds: productCategoryIds?.length ? productCategoryIds : undefined,
  }, { enabled: hasValidMooringId(mooringId) })

export const useGetSimplePositionDrawings = (
    searchInput?: string,
    positionType?: IPositionType,
    customerId?: number,
    productCategoryIds?: EntityId[],
  ) =>
    useGet<Array<IPositionDrawing>>(positionDrawingEndpoints.getSimplePositionDrawings(searchInput), {
      searchInput,
      positionType,
      customerId,
      productCategoryIds: productCategoryIds?.length ? productCategoryIds : undefined,
    })

  export const useGetDropDownPositionDrawings = (
    searchInput?: string,
    page: number = apiConsts.Page,
    pageSize: number = apiConsts.PageSize,
    positionType?: IPositionType,
    customerId?: EntityId,
    productCategoryIds?: EntityId[],
    mooringId?: EntityId,
  ) =>
    useGet<IPositionDrawing[]>(positionDrawingEndpoints.getDropDownPositionDrawings(searchInput), {
      searchInput,
      page,
      pageSize,
      positionType,
      customerId,
      productCategoryIds: productCategoryIds?.length ? productCategoryIds : undefined,
    }, { enabled: !hasValidMooringId(mooringId) })

export const useGetPositionDrawingInMooring = (
  customerId: EntityId,
  mooringId: EntityId,
  queryParams?: IPositionDrawingQueryParams,
) =>
  useGet<IPaginatedResult<IPositionDrawing>>(
    positionDrawingEndpoints.getPositionDrawingInMooring(customerId, mooringId),
    queryParams,
    {
      queryKey: generateQueryKey('position-drawings-in-mooring', {
        customerId,
        mooringId,
      }),
    },
  )

export const useGetPositionDrawing = (id: EntityId) =>
  useGet<IPositionDrawing>(positionDrawingEndpoints.getPositionDrawing(id))

export const useAddPositionDrawing = () =>
  usePost<IPositionDrawing, IPositionDrawing>(positionDrawingEndpoints.createPositionDrawing())

export const useUpdatePositionDrawing = (id: EntityId) =>
  usePut<IPositionDrawing, IPositionDrawing>(positionDrawingEndpoints.updatePositionDrawing(id))

export const useDeletePositionDrawing = () =>
  useDelete<IPositionDrawing>(positionDrawingEndpoints.deletePositionDrawing())

export const useGetSimplePositionDrawingsForProjects = (
  positionType?: IPositionType,
  searchInput?: string,
  enabled = false,
) => {
  return useGet<IPositionDrawing[]>(
    positionDrawingEndpoints.getSimplePositionDrawingsForProjects(),
    {
      positionType,
      searchInput,
    },
    {
      enabled: positionType !== undefined && enabled,
    },
  )
}
